<template>
  <VDialog placement="center" :title="$t('delete-user')" :visible @update:visible="hide">
    <p class="font-medium" style="margin-bottom: 30px">
      {{ $t('are-you-sure-you-want-to-delete-user-email', { email: user.name }) }}
      <br />
      <template v-if="user.groups.length">{{ $t('the-user-will-be-removed-from-all-groups-they-belong-to') }}</template>
    </p>

    <p v-if="!nCases">{{ $t('user-has-not-been-assigned-any-cases-therefore-no-cases-are-available-for-reassignment', { user: user.name }) }}</p>

    <VTableKeyValue v-if="nCases" style="margin-bottom: 15px">
      <VTableKeyValueRow :row-key="$t('count-cases-are-assigned-to-user', { count: nCases, user: user.name })">
        <VRadioGroup v-model="action" style="float: right; min-width: 265px" type="tabs-bordered">
          <VRadio label="reassign">{{ $t('reassign') }}</VRadio>
          <VRadio label="delete">{{ $t('delete') }}</VRadio>
        </VRadioGroup>
      </VTableKeyValueRow>
    </VTableKeyValue>

    <div v-if="action === 'reassign'" style="display: flex; align-items: center; justify-content: space-between">
      <p class="body-medium">{{ $t('reassign-cases-to') }}</p>
      <CaseAssigneeSelect v-model="assignee" :exclude-user-ids="new Set([user.id])" size="small" style="min-width: 300px" />
    </div>

    <VCheckbox v-if="action === 'delete'" v-model="confirmDeleteCases" class="mt-5" :label="$t('confirm-that-the-cases-assigned-to-user-can-be-deleted', { user: user.name })" />

    <template #footer>
      <VButton :label="$t('cancel')" type="secondary" @click="emit('update:visible', false)" />
      <VButton
        :disabled-help="{
          [$t('please-choose-an-option-on-what-to-do-with-the-cases-of-user')]: action === '' && nCases,
          [$t('please-confirm-that-you-want-to-delete-the-cases-of-this-user')]: action === 'delete' && !confirmDeleteCases,
        }"
        :label="$t('delete')"
        type="error"
        @click="submit"
      />
    </template>
  </VDialog>
</template>

<script setup lang="ts">
import { type Assignee } from '~/helpers/index.ts'
import { type User } from '~/types.ts'

interface Props {
  user: User
  visible: boolean
}

const { user, visible } = defineProps<Props>()

const emit = defineEmits<{
  'update:visible': [boolean]
}>()

const confirmDeleteCases = ref(false)
const assignee = ref<Assignee>({ type: 'group', value: null })
const nCases = ref(0)
const action = ref('')

async function fetch() {
  const { data } = await api.post('cases/searches', { user_id: [user.id], per_page: 1, organization_id: [stores.organization.id] })
  nCases.value = data.meta.total
}

watch(
  () => visible,
  (visible) => {
    if (visible) {
      fetch()
    }
  },
  { immediate: true },
)

function hide() {
  emit('update:visible', false)
  confirmDeleteCases.value = false
  action.value = ''
}

async function submit() {
  try {
    await stores.users.remove({
      id: user.id,
      cases_assignee: assignee.value.value,
      cases_assignee_type: assignee.value.value ? assignee.value.type : null,
      remove_cases: action.value === 'delete',
    })
    $message.success($t('the-user-email-has-been-successfully-deleted', { email: user.email }))
    hide()
  } catch {
    $message.error($t('api-error'))
  }
}
</script>
